class MobileCategoryNavigation extends HTMLElement {
  connectedCallback() {
    this.cleanup();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.scroller = this.querySelector('[data-category-scroll-container]');
    this.list = this.scroller?.querySelector('.menu-list__list');
    this.dialog = this.querySelector('[data-mobile-category-dialog]');
    this.dialogTitle = this.querySelector('[data-mobile-category-title]');
    this.dialogContent = this.querySelector('[data-mobile-category-content]');
    this.closeButton = this.querySelector('[data-mobile-category-close]');

    if (!this.scroller || !this.list || !this.dialog || !this.dialogTitle || !this.dialogContent) return;

    this.addEventListener('click', this.handleClick, { signal });
    this.scroller.addEventListener('scroll', this.scheduleScrollControlsUpdate, { passive: true, signal });
    this.dialog.addEventListener('cancel', this.handleDialogCancel, { signal });
    this.dialog.addEventListener('close', this.handleDialogClose, { signal });
    this.closeButton?.addEventListener('click', this.closeDialog, { signal });
    window.addEventListener('resize', this.scheduleScrollControlsUpdate, { passive: true, signal });
    window.addEventListener('orientationchange', this.scheduleScrollControlsUpdate, { passive: true, signal });

    this.resizeObserver = new ResizeObserver(this.scheduleScrollControlsUpdate);
    this.resizeObserver.observe(this.scroller);
    this.resizeObserver.observe(this.list);

    this.mutationObserver = new MutationObserver(this.scheduleScrollControlsUpdate);
    this.mutationObserver.observe(this.list, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (document.fonts) {
      document.fonts.addEventListener?.('loadingdone', this.scheduleScrollControlsUpdate, { signal });
      document.fonts.ready.then(() => {
        if (!signal.aborted) this.scheduleScrollControlsUpdate();
      });
    }

    this.scheduleScrollControlsUpdate();
    requestAnimationFrame(this.scheduleScrollControlsUpdate);
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.cleanup();
  }

  cleanup() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();

    if (this.updateFrame) cancelAnimationFrame(this.updateFrame);

    this.updateFrame = 0;
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const scrollButton = event.target.closest('[data-category-scroll-button]');
    if (scrollButton && this.contains(scrollButton)) {
      this.scrollCategories(scrollButton.dataset.categoryScrollButton);
      return;
    }

    const categoryTrigger = event.target.closest('[data-mobile-category-trigger]');
    if (categoryTrigger && this.contains(categoryTrigger)) {
      this.openCategory(categoryTrigger);
      return;
    }

    if (event.target === this.dialog) this.closeDialog();
  };

  handleDialogCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  handleDialogClose = () => {
    const triggerToRestore = this.activeTrigger;

    if (this.activeTrigger) this.activeTrigger.setAttribute('aria-expanded', 'false');

    this.activeTrigger = null;
    this.dialogContent.replaceChildren();

    if (triggerToRestore?.isConnected) triggerToRestore.focus({ preventScroll: true });
  };

  closeDialog = () => {
    if (this.dialog?.open) this.dialog.close();
  };

  openCategory(trigger) {
    const templateId = trigger.dataset.templateId;
    const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null;

    if (!(template instanceof HTMLTemplateElement)) return;

    if (this.activeTrigger && this.activeTrigger !== trigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.activeTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    this.dialogTitle.textContent = trigger.dataset.categoryTitle || trigger.textContent.trim();
    this.dialogContent.replaceChildren(template.content.cloneNode(true));

    if (!this.dialog.open) this.dialog.showModal();

    requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
  }

  scrollCategories(direction) {
    const distance = Math.max(180, this.scroller.clientWidth * 0.72);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.scroller.scrollBy({
      left: direction === 'previous' ? -distance : distance,
      behavior,
    });
  }

  scheduleScrollControlsUpdate = () => {
    if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(this.updateScrollControls);
  };

  updateScrollControls = () => {
    this.updateFrame = 0;

    if (!this.scroller?.isConnected) return;

    const tolerance = 2;
    const hasOverflow = this.scroller.scrollWidth > this.scroller.clientWidth + tolerance;
    const hasPrevious = hasOverflow && this.scroller.scrollLeft > tolerance;
    const hasNext =
      hasOverflow &&
      this.scroller.scrollLeft + this.scroller.clientWidth < this.scroller.scrollWidth - tolerance;

    this.toggleScrollControl('previous', hasPrevious);
    this.toggleScrollControl('next', hasNext);
  };

  toggleScrollControl(direction, isVisible) {
    const button = this.querySelector(`[data-category-scroll-button="${direction}"]`);
    const fade = this.querySelector(`[data-category-scroll-fade="${direction}"]`);

    if (button) button.hidden = !isVisible;
    if (fade) fade.hidden = !isVisible;
  }
}

if (!customElements.get('mobile-category-navigation')) {
  customElements.define('mobile-category-navigation', MobileCategoryNavigation);
}
